import React, { useEffect, useRef, useState } from 'react';
import { createDisplayName } from '@withjoy/joykit/utils';
import { StyledContainer } from './Text.styles';
import { useCombinedRefs } from '@shared/utils/hooks/useCombinedRefs';
import type { As } from '@shared/utils/forwardRef';
import { forwardStyledComponentRef } from '@shared/utils/forwardRef';
import type { BoxProps } from '../Box';

export type TextV2Props<Component extends As = 'p'> = Merge<
  Omit<BoxProps<Component>, 'as' | 'css'>,
  {
    /**
     * Override the default HTML element type
     * @default div
     */
    tagName?: React.ElementType;

    children?: React.ReactNode;

    // /**
    //  * Apply the theme typography styles
    //  * @default 'bodySM'
    //  */
    // variant?: ResponsiveValue<TypographyVariant>;

    /**
     * Whether to render an ellipsis when the text exceeds the width of the container
     *
     * Note: This will only apply to block containers. Span will not work.
     * https://www.w3.org/TR/css-overflow-3/#text-overflow
     *
     * @default false
     */
    truncate?: boolean;
  }
>;

/**
 * TODO:
 * - Create variant -> component mapping
 */

export const TextV2 = forwardStyledComponentRef<'p', TextV2Props>(({ as, tagName = 'p', truncate = false, children, typographyVariant, ...restProps }, ref) => {
  // The full text is exposed as `title` only when a truncated text is actually clipped. Measured
  // after every render on purpose (the text can change without a prop changing), but only when
  // `truncate` is on: this is the most rendered component on the guest site, and storing the text
  // content in state for every instance gave each one a second render on mount and another
  // whenever a parent re-rendered it with different text.
  const [overflowTitle, setOverflowTitle] = useState<string | undefined>(undefined);
  const containerRef = useRef<HTMLParagraphElement>(null);
  const multiRef = useCombinedRefs(ref, containerRef);

  useEffect(() => {
    const node = multiRef.current;
    // With white-space="no-wrap", text renders on one line regardless of container size, so an
    // overflowing text has a scroll width larger than its client width.
    const nextTitle = truncate && node && node.scrollWidth > node.clientWidth ? node.textContent || undefined : undefined;
    setOverflowTitle(previous => (previous === nextTitle ? previous : nextTitle));
  });

  return (
    <StyledContainer
      ref={multiRef}
      title={overflowTitle}
      truncate={truncate}
      as={as || tagName}
      typographyVariant={typographyVariant || 'body2'}
      {...restProps}
    >
      {children}
    </StyledContainer>
  );
});

TextV2.displayName = createDisplayName('Text');

// typographyVariant
